import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatLlmTrace, formatMsgTime } from './AIAgentConstants';
import { markdownComponents } from './AIAgentMarkdown';

var copyText = function(text) {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
};

export default function ChatMessage({ msg, selectMode, selectedMsgs, toggleSelectMsg, setActiveTab, onResend, onCopy, onShare, onForwardSms, showAllThinking }) {
  const failedTools = (msg.toolCalls || []).filter(tc => tc && tc.error);
  const traceText = formatLlmTrace(msg.llmTrace);
  const timeText = formatMsgTime(msg.timestamp);
  var _normModel = function(s) {
    // Strip provider prefix (openai/, anthropic/, google/, openrouter/, etc), preview suffix,
    // and tag suffix so 'openai/gpt-4o-mini' and 'gpt-4o-mini' compare equal.
    return String(s || '').toLowerCase().replace(/^[^/]+\//, '').replace(/-preview$/, '').replace(/[:@].*$/, '');
  };
  var modelSubstituted = false;
  var modelRequested = '';
  var modelEffective = '';
  if (msg.role === 'assistant' && msg.llmTrace) {
    modelRequested = msg.llmTrace.requestedModel || '';
    modelEffective = msg.llmTrace.effectiveModel || '';
    if (modelRequested && modelEffective && _normModel(modelRequested) !== _normModel(modelEffective)) {
      modelSubstituted = true;
    }
  }
  const [copied, setCopied] = React.useState(false);
  const [showThinking, setShowThinking] = React.useState(false);
  React.useEffect(() => { if (typeof showAllThinking === 'boolean') setShowThinking(showAllThinking); }, [showAllThinking]);
  // Render pending assistant bubbles as a visible "thinking" placeholder.
  // Previously these returned null and the thinking-activity bar far away handled state —
  // users reported never seeing in-progress feedback. A bubble exactly where the reply
  // will land gives an unambiguous signal that the request is accepted and processing.
  if (msg.role === 'assistant' && msg.pending) {
    return (
      <div className={`chat-msg ${msg.role} pending`}>
        <div className="chat-msg-content thinking-placeholder">
          <span className="thinking-dots" aria-label="Thinking">
            <span/><span/><span/>
          </span>
          <span className="thinking-label">Thinking…</span>
        </div>
      </div>
    );
  }
  if (msg.role === 'assistant' && !msg.error && !(msg.content && String(msg.content).trim()) && !(msg.toolCalls && msg.toolCalls.length)) return null;
   if (msg.role === 'system') {
     return (
       <div className={`chat-msg ${msg.role}`}>
         <div className="chat-msg-content">{msg.content}</div>
       </div>
     );
   }
  const hasReasoning = !!(msg.reasoning || msg.reasoning_content);
  const handleCopy = (e) => {
    e.stopPropagation();
    if (!navigator.clipboard?.writeText) { if (onCopy) onCopy(msg); return; }
    navigator.clipboard.writeText(msg.content || '').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500); if (onCopy) onCopy(msg);
    }).catch(() => { if (onCopy) onCopy(msg); });
  };
  const handleResend = (e) => { e.stopPropagation(); if (onResend) onResend(msg); };
  const handleShare = (e) => { e.stopPropagation(); if (onShare) onShare({ text: msg.content || '', msg: msg }); };
  const handleToggleThinking = (e) => { e.stopPropagation(); setShowThinking(v => !v); };

  var buildErrorText = function() {
    var parts = [];
    if (failedTools.length > 0) {
      parts.push('## Tool Errors');
      failedTools.forEach(function(tc) {
        parts.push('- **' + (tc.name || 'unknown') + '**: ' + (typeof tc.error === 'string' ? tc.error : JSON.stringify(tc.error)));
      });
      parts.push('');
    }
    if (msg.error) {
      parts.push('## Error');
      parts.push(msg.content || 'No details');
      parts.push('');
    }
    if (msg.llmTrace) parts.push('## Trace\n' + JSON.stringify(msg.llmTrace, null, 2));
    return parts.join('\n');
  };

  var hasErrors = failedTools.length > 0 || msg.error;

  return (
    <div className={`chat-msg ${msg.role} ${msg.error ? 'error' : ''} ${selectMode ? 'selectable' : ''} ${selectedMsgs?.has(msg.timestamp) ? 'selected' : ''}`} onClick={selectMode ? () => toggleSelectMsg(msg.timestamp) : undefined}>
       <div className="chat-msg-content">
         {msg.role === 'assistant' && hasReasoning && (
           <div className="chat-reasoning">
             <button className="chat-reasoning-toggle" onClick={() => setShowThinking(!showThinking)}>
               <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points={showThinking ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/></svg>
               {showThinking ? 'Thinking' : 'Show thinking'}
             </button>
             {showThinking && <div className="chat-reasoning-content">{String(msg.reasoning || msg.reasoning_content)}</div>}
           </div>
         )}
         {msg.role === 'assistant' && msg.pending ? (
           <span className="live-dot-pulse">...</span>
         ) : msg.role === 'assistant' ? (
           <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
         ) : msg.content}
         {modelSubstituted && (
           <div style={{marginTop:8,padding:'8px 10px',background:'#fff8e1',border:'1px solid #f0c14b',borderRadius:8,fontSize:12,color:'#8b6914',lineHeight:1.4}}>
             <strong>Model substituted.</strong> You picked <code style={{background:'#fffaeb',padding:'1px 4px',borderRadius:3}}>{String(modelRequested).replace(/^[^/]+\//,'')}</code> but it's not available right now — replied with <code style={{background:'#fffaeb',padding:'1px 4px',borderRadius:3}}>{String(modelEffective).replace(/^[^/]+\//,'')}</code>.
           </div>
         )}
         {hasErrors && (
           <div className="tool-errors">
             {failedTools.map((tc, ti) => (
               <div key={ti} className="tool-error-block">
                 <div className="tool-error-header">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c62828" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                   <span className="tool-error-name">{tc.name}</span>
                   <span className="tool-error-msg">{typeof tc.error === 'string' ? tc.error : JSON.stringify(tc.error)}</span>
                   <button className="chat-action-btn" onClick={function(e) { e.stopPropagation(); copyText((tc.name || 'error') + ': ' + (typeof tc.error === 'string' ? tc.error : JSON.stringify(tc.error))); }} title="Copy this error">
                     <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                   </button>
                 </div>
                 {tc.error && tc.error.includes('Access denied') && (
                   <button className="btn-fix-access" onClick={() => setActiveTab('Settings')}>Fix in Access tab</button>
                 )}
               </div>
             ))}
             {hasErrors && (
               <button className="chat-copy-errors-btn" onClick={function(e) { e.stopPropagation(); copyText(buildErrorText()); }} title="Copy all errors as Markdown">
                 <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                 Copy all as MD
               </button>
             )}
           </div>
         )}
        <div className="chat-msg-footer">
          {timeText && <span className="chat-msg-time">{timeText}</span>}
          {traceText && <span className="chat-llm-trace">{traceText}</span>}
          <span className="chat-msg-actions">
            <button className="chat-action-btn" onClick={handleCopy} title="Copy">
              {copied ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#43a047" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>}
            </button>
            {msg.role === 'user' && onResend && (
              <button className="chat-action-btn" onClick={handleResend} title="Resend">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/></svg>
              </button>
            )}
            {hasReasoning && (
              <button className={`chat-action-btn ${showThinking ? 'active' : ''}`} onClick={handleToggleThinking} title={showThinking ? 'Hide thinking' : 'Show thinking'}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </button>
            )}
            {onForwardSms && msg.role === 'assistant' && msg.content && (
              <button className="chat-action-btn chat-forward-sms-btn" onClick={function(e) { e.stopPropagation(); onForwardSms(msg.content); }} title="Forward as SMS">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
            )}
            {onShare && (
              <button className="chat-action-btn" onClick={handleShare} title="Share">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
